import { Content } from "@r8er/components/marketing/content";
import styles from "@r8er/components/marketing/content/content.module.css";
import {
  CtaBlock,
  Ctas,
  LargeButton,
  SignUpButton,
} from "@r8er/components/marketing/ctas";
import { Hero } from "@r8er/components/marketing/hero";
import { Layout } from "@r8er/components/marketing/layout";
import { Newsletter } from "@r8er/components/marketing/newsletter";
import Head from "next/head";

export default function Investors() {
  return (
    <div>
      <Head>
        <title>Investors - R8ER</title>
        <link rel="icon" href="/favicon.png" />
      </Head>

      <Layout>
        <Hero isSlim title="Investors" img="/investors-hero.png" />

        <Content
          isFullWidth
          isNarrow
          isCentered
        >
          <p
            className="p--standard"
            style={{
              fontSize: "calc(1.75 * var(--font-size-base))",
              margin: "0 auto",
              letterSpacing: "-.25px",
            }}
          >
            As a registered investor with R8ER, you'll gain exclusive access to
            opportunities that have been carefully vetted.
          </p>
          <SignUpButton text="Request an Invitation" />
        </Content>

        <Content
          outerImage="/investors-hand.png"
          className={styles.investorHandSection}
        >
          <h2
            className="h2--standard"
            style={{ color: "var(--brand-secondary)" }}
          >
            Invest in the Next Generation of Health and Climate Innovation
          </h2>
          <div style={{ maxWidth: "575px" }}>
            <p className="p--standard">
              Join an exclusive network of family offices, high-net-worth
              individuals, institutional investors and specialist funds
              investing from £50,000 to £10 million into innovative health and
              climate tech companies.
            </p>
            <p className="p--standard">
              R8ER is free to join and designed to help investors discover
              high-quality opportunities more efficiently.
            </p>
          </div>
        </Content>

        <Content
          isGrey
          isRightAligned
          backgroundImage="/investors-meeting.png"
        >
          <h2 className="h2--standard">
            Access Curated Opportunities Matched to Your Investment Strategy
          </h2>
          <p className="p--standard">
            Every investor tells us exactly what they are looking for, including
            sector, stage, geography and investment size.
          </p>
          <p className="p--standard">
            Rather than receiving a broad pipeline, you'll only see companies
            that match your investment criteria.
          </p>
          <p className="p--standard">
            R8ER has become a trusted platform for family offices seeking
            professionally reviewed investment opportunities, alongside venture
            capital funds, institutional investors and experienced private
            investors.
          </p>
        </Content>

        <Content backgroundImage="/investors-seats-2.png">
          <h2 className="h2--standard">Independent Expert Insight</h2>
          <p className="p--standard">
            Every opportunity is supported by insight from five internationally
            recognised Key Opinion Leaders and senior specialists selected
            specifically for that company's technology and market. Depending on
            the company, these may include experts across:
          </p>
          <ul className={`ul--standard ${styles.compactMarketingList}`}>
            <li>Investment</li>
            <li>Academic &amp; Technical</li>
            <li>Clinical (for healthcare companies)</li>
            <li>Commercialisation</li>
            <li>Regulatory &amp; Compliance</li>
            <li>Climate or Industry Specialists</li>
          </ul>
          <p className="p--standard">
            Their combined expertise provides investors with a structured
            summary highlighting strengths, considerations and key areas for
            further investigation, helping you assess opportunities more
            efficiently before conducting your own due diligence.
          </p>
        </Content>

        <Content
          isBlue
          isRightAligned
          backgroundImage="/investors-meeting-blue.png"
        >
          <h2 className="h2--standard">
            Discretion is central to the R8ER platform
          </h2>
          <p className="p--standard">
            You decide which opportunities you wish to explore and which
            founders you choose to contact. Companies cannot see which investors
            are on the platform or who has viewed their profile, allowing you to
            evaluate opportunities privately and engage only when you are ready.
          </p>
          <p className="p--standard">
            There is no obligation to contact a company or make an
            investment—you're always in control of your investment journey.
          </p>
        </Content>

        <Content backgroundImage="/investors-negotiation.png">
          <h2 className="h2--standard">More Than a Deal Platform</h2>
          <p className="p--standard">
            As a R8ER investor you also receive access to:
          </p>
          <ul className={`ul--standard ${styles.compactMarketingList}`}>
            <li>Monthly investor pitching sessions (London and online)</li>
            <li>MegaPitch investor events</li>
            <li>Opportunities to refer or sponsor companies</li>
            <li>
              Optional specialist expert reviews on individual opportunities
            </li>
            <li>Direct communication with founders</li>
            <li>
              Complete discretion over which companies you choose to engage
            </li>
          </ul>
        </Content>

        <Content
          backgroundImage="/investor-next.png"
          isRightAligned
          className={styles.membershipSection}
        >
          <h2 className="h2--standard">Membership</h2>
          <p className="p--standard">R8ER is invitation only.</p>
          <p className="p--standard">We primarily work with:</p>
          <ul className={`ul--standard ${styles.compactMarketingList}`}>
            <li>Family Offices</li>
            <li>High Net Worth Individuals</li>
            <li>Private Investment Firms</li>
            <li>Venture Capital &amp; Private Equity Funds</li>
            <li>Institutional Investors</li>
            <li>Select Corporate Investors</li>
          </ul>
          <p className="p--standard">
            Investors must qualify as an Investment Professional, Certified High
            Net Worth Individual or Certified Sophisticated Investor.
          </p>
          <p className="p--standard">
            The minimum investment ticket size on the R8ER platform is £50,000.
          </p>
          <p className="p--standard">
            Investors looking for smaller-ticket opportunities from £5,000 may
            also be interested in joining the{" "}
            <strong>
              <a href="https://www.vetted.investments">
                Vetted Investments Syndicate (click here for information)
              </a>
            </strong>
            , which provides access to a highly selective group of companies.
          </p>
          <LargeButton
            text="Request an Invitation"
            style={{ marginTop: "2.5rem", marginBottom: "1rem" }}
          />
        </Content>

        <Ctas>
          <CtaBlock
            title="Register to request an invitation"
            titleClasName={styles.wideTitle}
            img="/cta-investors.png"
            ctaItems={[{ text: "Request an Invitation", link: "/auth/signup" }]}
            isLight
          />
          <CtaBlock
            title="Speak to a member of the R8ER team"
            img="/cta-contact.png"
            ctaItems={[
              { text: "Contact Us", link: "/contact" },
              { text: "Request information", link: "/contact" },
            ]}
          />
        </Ctas>

        <Newsletter />
      </Layout>
    </div>
  );
}
